import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MdChevronRight, MdKeyboardArrowDown, MdContentCopy, MdCode, MdArrowBack, MdSearch } from "react-icons/md";
import { HiKey, HiDocumentText } from "react-icons/hi";

const ApiDocumentation = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("overview");
  const [expandedSections, setExpandedSections] = useState({
    authentication: true,
    surveys: true,
    surveyGroups: true,
    sessions: true,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const CodeBlock = ({ children, language = "json" }) => (
    <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4 overflow-x-auto relative group">
      <button
        onClick={() => copyToClipboard(children)}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-[#a1a1a6] hover:text-white"
        title="Copy to clipboard"
      >
        <MdContentCopy size={16} />
      </button>
      <pre className="text-[#a1a1a6] text-[13px] font-mono whitespace-pre-wrap">{children}</pre>
    </div>
  );

  const ResponseExample = ({ title, status, children }) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h4 className="text-white text-[16px] font-[600]">{title}</h4>
        <span className="text-[#a1a1a6] text-[12px] font-mono bg-[#2d2d2f] px-2 py-1 rounded">
          {status}
        </span>
      </div>
      <CodeBlock>{children}</CodeBlock>
    </div>
  );

  const documentationContent = {
    overview: {
      title: "Overview",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            The Public DIY APIs provide a comprehensive RESTful interface for programmatically managing your survey operations on the DIY platform. Whether you're building integrations, automating survey workflows, or creating custom applications, these APIs enable you to seamlessly interact with your surveys, survey groups, and session data.
          </p>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">What You Can Do</h3>
            <ul className="list-disc list-inside space-y-2 text-[#e5e5e5] text-[14px]">
              <li><strong>Create and Manage Surveys</strong>: Build surveys programmatically with full control over configuration, including target audience, study type, industry, and survey parameters</li>
              <li><strong>Bulk Operations</strong>: Create multiple surveys simultaneously within the same survey group, perfect for multi-country studies or A/B testing</li>
              <li><strong>Survey Groups</strong>: Organize related surveys into groups for better management and reporting</li>
              <li><strong>Real-time Data Access</strong>: Retrieve survey sessions with advanced filtering capabilities to monitor performance and analyze responses</li>
              <li><strong>Flexible Updates</strong>: Modify survey details including status, LOI (Length of Interview), IR (Incidence Rate), and live/test URLs</li>
            </ul>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Key Features</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
                <h4 className="text-white text-[15px] font-[600] mb-2">⚡ Instant Sampling</h4>
                <p className="text-[#a1a1a6] text-[13px]">All API-created surveys are automatically configured as "Instant Sampling" surveys, ready for immediate data collection</p>
              </div>
              <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
                <h4 className="text-white text-[15px] font-[600] mb-2">🎯 Smart Defaults</h4>
                <p className="text-[#a1a1a6] text-[13px]">Automatic assignment of project managers, default qualifications, and allocations to streamline your workflow</p>
              </div>
              <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
                <h4 className="text-white text-[15px] font-[600] mb-2">🔐 Secure Authentication</h4>
                <p className="text-[#a1a1a6] text-[13px]">API key-based authentication with support for multiple keys per organization</p>
              </div>
              <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
                <h4 className="text-white text-[15px] font-[600] mb-2">📊 Comprehensive Error Handling</h4>
                <p className="text-[#a1a1a6] text-[13px]">Detailed error responses with validation messages for easy debugging</p>
              </div>
              <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4 md:col-span-2">
                <h4 className="text-white text-[15px] font-[600] mb-2">📄 Pagination Support</h4>
                <p className="text-[#a1a1a6] text-[13px]">Efficient data retrieval with built-in pagination for large datasets</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Use Cases</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#007aff] mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="text-white text-[14px] font-[600] mb-1">Automated Survey Creation</h4>
                  <p className="text-[#a1a1a6] text-[13px]">Integrate survey creation into your existing workflows and systems</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#007aff] mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="text-white text-[14px] font-[600] mb-1">Multi-Market Studies</h4>
                  <p className="text-[#a1a1a6] text-[13px]">Quickly create surveys across multiple countries with consistent configurations</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#007aff] mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="text-white text-[14px] font-[600] mb-1">Real-time Monitoring</h4>
                  <p className="text-[#a1a1a6] text-[13px]">Build dashboards that pull live session data for performance tracking</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#007aff] mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="text-white text-[14px] font-[600] mb-1">Workflow Automation</h4>
                  <p className="text-[#a1a1a6] text-[13px]">Automate repetitive tasks like survey status updates and group management</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#007aff] mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="text-white text-[14px] font-[600] mb-1">Custom Integrations</h4>
                  <p className="text-[#a1a1a6] text-[13px]">Connect the DIY platform with your CRM, marketing automation tools, or custom applications</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-6">
            <h3 className="text-white text-[18px] font-[600] mb-3">Base URL</h3>
            <code className="text-[#a1a1a6] text-[14px] font-mono break-all">
              https://public-quanto-diy.miratsapiservices.com/api/diy/public
            </code>
            <p className="text-[#a1a1a6] text-[12px] mt-2 italic">
              Note: Replace <code className="text-[#a1a1a6]">public-quanto-diy.miratsapiservices.com</code> with your actual API domain.
            </p>
          </div>
        </div>
      ),
    },
    authentication: {
      title: "Authentication",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            All API requests require authentication using an API key. You can generate API keys from the Developer section in your account settings.
          </p>
          
          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">API Key Format</h3>
            <p className="text-[#a1a1a6] text-[14px] mb-4">API keys can be provided in two ways:</p>
            <div className="space-y-4">
              <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
                <p className="text-white text-[14px] font-[500] mb-2">1. X-API-Key Header (Recommended)</p>
                <code className="text-[#a1a1a6] text-[13px] font-mono block">
                  X-API-Key: your-api-key-here
                </code>
              </div>
              <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
                <p className="text-white text-[14px] font-[500] mb-2">2. Authorization Bearer Header</p>
                <code className="text-[#a1a1a6] text-[13px] font-mono block">
                  Authorization: Bearer your-api-key-here
                </code>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Generating API Keys</h3>
            <ol className="list-decimal list-inside space-y-2 text-[#e5e5e5] text-[14px]">
              <li>Log in to your DIY account</li>
              <li>Navigate to <strong>Profile</strong> → <strong>Developer</strong> section</li>
              <li>Click <strong>Generate API Key</strong></li>
              <li>Provide a name for your API key</li>
              <li>Copy and securely store your API key (it will only be shown once)</li>
            </ol>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Security Best Practices</h3>
            <ul className="list-disc list-inside space-y-2 text-[#e5e5e5] text-[14px]">
              <li>Keep your API keys secure and never commit them to version control</li>
              <li>Rotate API keys regularly</li>
              <li>Use different API keys for different environments (development, staging, production)</li>
              <li>Revoke unused or compromised API keys immediately</li>
            </ul>
          </div>
        </div>
      ),
    },
    responseFormat: {
      title: "Response Format",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            All API responses follow a consistent format.
          </p>
          
          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Success Response</h3>
            <CodeBlock>{`{
  "success": true,
  "data": {
    // Response data here
  }
}`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Error Response</h3>
            <CodeBlock>{`{
  "success": false,
  "error": {
    "code": "ERROR_CODE",
    "message": "Human-readable error message",
    "details": [
      {
        "path": "field.path",
        "message": "Validation error message"
      }
    ]
  }
}`}</CodeBlock>
          </div>
        </div>
      ),
    },
    errorCodes: {
      title: "Error Codes",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            All API errors follow a consistent format with specific error codes.
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-[#2d2d2f]">
                  <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Code</th>
                  <th className="text-left text-white text-[14px] font-[600] py-2 px-4">HTTP Status</th>
                  <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#2d2d2f]">
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>MISSING_API_KEY</code></td>
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4">401</td>
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4">API key is required but not provided</td>
                </tr>
                <tr className="border-b border-[#2d2d2f]">
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>INVALID_API_KEY</code></td>
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4">401</td>
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4">API key is invalid, expired, or inactive</td>
                </tr>
                <tr className="border-b border-[#2d2d2f]">
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>VALIDATION_ERROR</code></td>
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4">400</td>
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Request validation failed</td>
                </tr>
                <tr className="border-b border-[#2d2d2f]">
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>SURVEY_NOT_FOUND</code></td>
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4">404</td>
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Survey with the specified ID not found</td>
                </tr>
                <tr className="border-b border-[#2d2d2f]">
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>SURVEY_GROUP_NOT_FOUND</code></td>
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4">404</td>
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Survey group with the specified ID not found</td>
                </tr>
                <tr className="border-b border-[#2d2d2f]">
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>AUTH_ERROR</code></td>
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4">500</td>
                  <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Authentication error occurred</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ),
    },
    commonLibrary: {
      title: "Common Library",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            This section provides reference values for common fields used across the API.
          </p>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Survey Status</h3>
            <p className="text-[#a1a1a6] text-[14px] mb-3">Valid values for <code className="text-[#a1a1a6]">surveyStatus</code>:</p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#2d2d2f]">
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Value</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>awarded</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Survey has been awarded but not yet live (default)</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>live</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Survey is currently active and collecting responses</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>paused</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Survey is temporarily paused</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>closed</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Survey is closed</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>completed</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Survey has been completed</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>archived</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Survey has been archived</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Study Type</h3>
            <p className="text-[#a1a1a6] text-[14px] mb-3">Valid values for <code className="text-[#a1a1a6]">setup.studyType</code>:</p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#2d2d2f]">
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Value</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>adhoc</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Adhoc study (default)</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>Ad_Effectiveness_Research</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Ad Effectiveness Research</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>community_build</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Community Build</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>face_to_face</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Face to Face</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>IHUT</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">In-Home Usage Test</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>internal_use</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Internal Use</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>incidence_check</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Incidence Check</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>qualitative_screening</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Qualitative Screening</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>recontact</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Recontact</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>recruit_panel</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Recruit - Panel</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>tracking_weekly</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Tracking - Weekly</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>tracking_monthly</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Tracking - Monthly</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>tracking_quaterly</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Tracking - Quarterly</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>tracking_yearly</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Tracking - Yearly</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>tracking_biyearly</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Tracking - Biyearly</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>wave_study</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Wave Study</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Industry</h3>
            <p className="text-[#a1a1a6] text-[14px] mb-3">Valid values for <code className="text-[#a1a1a6]">setup.industry</code>:</p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#2d2d2f]">
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Value</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>others</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Others (default)</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>agriculture</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Agriculture</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>automotive_and_mobility</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Automotive & Mobility</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>consumer_package_goods</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Consumer Package Goods</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>construction</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Construction</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>energy_and_utilities</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Energy & Utilities</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>education</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Education</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>fashion_beauty_and_luxury</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Fashion, Beauty & Luxury</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>finance</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Finance</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>government_and_public_sector</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Government & Public Sector</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>health</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Health</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>hospitality_leisure_and_travel</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Hospitality, Leisure & Travel</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>media</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Media</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>retail</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Retail</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>sport_and_leisure</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Sport & Leisure</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>technology_and_telecoms</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Technology & Telecoms</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Target Audience</h3>
            <p className="text-[#a1a1a6] text-[14px] mb-3">Valid values for <code className="text-[#a1a1a6]">setup.targetAudience</code>:</p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#2d2d2f]">
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Value</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>B2C</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Business to Consumer (default)</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>B2B</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Business to Business</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[#a1a1a6] text-[13px] mt-3 italic">
              <strong>Note:</strong> The target audience determines which automatic qualifications are added to the survey:
            </p>
            <ul className="list-disc list-inside space-y-1 text-[#e5e5e5] text-[14px] ml-4 mt-2">
              <li><strong>B2C</strong>: Age (18-75) + Gender qualifications</li>
              <li><strong>B2B</strong>: Age (18-60) + Gender + Employment Status qualifications</li>
            </ul>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Languages</h3>
            <p className="text-[#a1a1a6] text-[14px] mb-3">Common language values for <code className="text-[#a1a1a6]">setup.language</code>:</p>
            <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {["English (default)", "Albanian", "Arabic", "Basque", "Belarusian", "Bengali", "Bosnian", "Bulgarian", "Catalan", "Croatian", "Czech", "Danish", "Dutch", "Estonian", "Finnish", "French", "German", "Greek", "Hindi", "Hungarian", "Icelandic", "Irish", "Italian", "Japanese", "Latvian", "Lithuanian", "Maltese", "Mandarin Chinese", "Norwegian", "Portuguese", "Russian", "Spanish"].map((lang, idx) => (
                  <div key={idx} className="text-[#a1a1a6] text-[13px]">
                    <code>{lang}</code>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    surveyDefaults: {
      title: "Survey Creation Defaults",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            All API-created surveys automatically receive the following defaults and configurations:
          </p>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Automatic Qualifications</h3>
            <div className="space-y-4">
              <div>
                <p className="text-white text-[15px] font-[500] mb-2">B2C Surveys (default):</p>
                <ul className="list-disc list-inside space-y-1 text-[#e5e5e5] text-[14px] ml-4">
                  <li><strong>Age Qualification</strong>: Age range 18-75 years</li>
                  <li><strong>Gender Qualification</strong>: Male/Female options</li>
                </ul>
              </div>
              <div>
                <p className="text-white text-[15px] font-[500] mb-2">B2B Surveys:</p>
                <ul className="list-disc list-inside space-y-1 text-[#e5e5e5] text-[14px] ml-4">
                  <li><strong>Age Qualification</strong>: Age range 18-60 years</li>
                  <li><strong>Gender Qualification</strong>: Male/Female options</li>
                  <li><strong>Employment Status Qualification</strong>: Full-time, part-time, self-employed, military, unemployed, homemaker, retired, student, disabled, or prefer not to answer</li>
                </ul>
              </div>
            </div>
            <p className="text-[#a1a1a6] text-[13px] mt-3 italic">
              These qualifications are automatically added to match the frontend behavior and cannot be disabled for API-created surveys.
            </p>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Default Language</h3>
            <p className="text-[#e5e5e5] text-[14px]">
              <strong>Language</strong>: <code className="text-[#a1a1a6]">"English"</code> (unless explicitly specified in the request)
            </p>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Default Project Manager</h3>
            <p className="text-[#e5e5e5] text-[14px] mb-2">If no project manager is provided in the request:</p>
            <ol className="list-decimal list-inside space-y-1 text-[#e5e5e5] text-[14px] ml-4">
              <li>First, the system attempts to assign an <strong>admin user</strong> from your organization</li>
              <li>If no admin exists, it assigns the <strong>oldest account</strong> (earliest <code className="text-[#a1a1a6]">createdAt</code>) in your organization</li>
            </ol>
            <p className="text-[#e5e5e5] text-[14px] mt-2">
              If a project manager is explicitly provided in the request, that value is used.
            </p>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Expected Matrix Fields</h3>
            <p className="text-[#e5e5e5] text-[14px] mb-4">
              The <code className="text-[#a1a1a6]">expectedMatrix</code> object contains important survey planning metrics:
            </p>
            <div className="space-y-4">
              <div>
                <p className="text-white text-[15px] font-[500] mb-1">
                  <code className="text-[#a1a1a6]">expectedCompletes</code> (required, number, min: 1)
                </p>
                <p className="text-[#e5e5e5] text-[14px] ml-4">
                  The target number of completed survey responses. Used to calculate allocation numbers and quotas. Example: <code className="text-[#a1a1a6]">100</code> means you want 100 completed responses.
                </p>
              </div>
              <div>
                <p className="text-white text-[15px] font-[500] mb-1">
                  <code className="text-[#a1a1a6]">expectedIncidenceRate</code> (optional, number, range: 0-100)
                </p>
                <p className="text-[#e5e5e5] text-[14px] ml-4">
                  Also known as <strong>IR (Incidence Rate)</strong>. Represents the percentage of respondents who qualify for your survey. Typical values: 20-80%. Example: <code className="text-[#a1a1a6]">50</code> means 50% of respondents are expected to qualify. Used for feasibility calculations and quota planning.
                </p>
              </div>
              <div>
                <p className="text-white text-[15px] font-[500] mb-1">
                  <code className="text-[#a1a1a6]">expectedCompletionLOI</code> (optional, number, min: 0)
                </p>
                <p className="text-[#e5e5e5] text-[14px] ml-4">
                  Also known as <strong>LOI (Length of Interview)</strong>. Represents the estimated time (in minutes) it takes to complete the survey. Typical values: 5-30 minutes. Example: <code className="text-[#a1a1a6]">10</code> means the survey takes approximately 10 minutes to complete. Used for respondent compensation calculations and survey planning.
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    createSurvey: {
      title: "Create Survey",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            Create a new survey with automatic defaults for qualifications, language, and project manager assignment.
          </p>
          
          <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
            <p className="text-white text-[14px] font-[500] mb-2">Endpoint</p>
            <code className="text-[#a1a1a6] text-[13px] font-mono">POST /surveys</code>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Headers</h3>
            <CodeBlock>{`Content-Type: application/json
X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Request Body Schema</h3>
            <CodeBlock>{`{
  "surveyName": "My Survey Name",
  "setup": {
    "country": "United States",
    "countryCode": "US",
    "language": "English",
    "testUrl": "https://example.com/test",
    "liveUrl": "https://example.com/live",
    "surveyCpi": "2.50",
    "clientCpi": "3.00",
    "studyType": "adhoc",
    "targetAudience": "B2C",
    "businessUnit": "Marketing",
    "industry": "retail",
    "personalInformation": false
  },
  "expectedMatrix": {
    "expectedCompletes": 100,
    "expectedIncidenceRate": 50,
    "expectedCompletionLOI": 10
  },
  "peoples": {
    "projectManager": "optional-object-id"
  },
  "surveyGroupId": "optional-group-id",
  "surveyStatus": "awarded"
}`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Required Fields</h3>
            <ul className="list-disc list-inside space-y-1 text-[#e5e5e5] text-[14px]">
              <li><code className="text-[#a1a1a6]">surveyName</code> (string, min 1 character)</li>
              <li><code className="text-[#a1a1a6]">setup.country</code> (string, min 1 character)</li>
              <li><code className="text-[#a1a1a6]">setup.countryCode</code> (string, min 1 character)</li>
              <li><code className="text-[#a1a1a6]">expectedMatrix.expectedCompletes</code> (number, min 1)</li>
            </ul>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Optional Fields</h3>
            <ul className="list-disc list-inside space-y-1 text-[#e5e5e5] text-[14px]">
              <li><code className="text-[#a1a1a6]">setup.language</code> (string, default: "English")</li>
              <li><code className="text-[#a1a1a6]">setup.targetAudience</code> (enum: "B2C" or "B2B", default: "B2C") - Determines automatic qualifications</li>
              <li><code className="text-[#a1a1a6]">expectedMatrix.expectedIncidenceRate</code> (number, 0-100) - IR (Incidence Rate) percentage</li>
              <li><code className="text-[#a1a1a6]">expectedMatrix.expectedCompletionLOI</code> (number, min 0) - LOI (Length of Interview) in minutes</li>
              <li><code className="text-[#a1a1a6]">peoples.projectManager</code> (string, ObjectId) - If not provided, defaults to admin or oldest account</li>
            </ul>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Response Examples</h3>
            <div className="space-y-6">
              <ResponseExample title="Success Response" status="201 Created">
{`{
  "success": true,
  "data": {
    "_id": "6969db791ca7128681350d0d",
    "surveyName": "My Survey Name",
    "surveyNumber": 17685451455780060,
    "projectNumber": 17685451460530060,
    "surveyStatus": "awarded",
    "setup": {
      "country": "United States",
      "countryCode": "US",
      "language": "English",
      "studyType": "adhoc",
      "targetAudience": "B2C",
      "businessUnit": "Marketing",
      "industry": "retail",
      "personalInformation": false,
      "surveyCpi": "2.50",
      "testUrl": "https://example.com/test",
      "liveUrl": "https://example.com/live"
    },
    "expectedMatrix": {
      "expectedCompletes": 100,
      "expectedIncidenceRate": 50,
      "expectedCompletionLOI": 10
    },
    "peoples": {
      "projectManager": "John Doe"
    },
    "qualification": [
      {
        "_id": "6969db791ca7128681350d0e",
        "question": "656d6d54f1cdf363aab57950",
        "validOptions": [{ "from": 18, "to": 75 }],
        "displayOptions": [],
        "status": true
      },
      {
        "_id": "6969db791ca7128681350d0f",
        "question": "656d6d54f1cdf363aab57954",
        "validOptions": ["Male", "Female"],
        "displayOptions": ["Male", "Female", "others"],
        "status": true,
        "condition": { "min": "", "max": "" }
      }
    ],
    "allocation": [
      {
        "_id": "6969db7a1ca7128681350d16",
        "allocation": {
          "number": 100,
          "percentage": 100
        },
        "cpi": 2.5,
        "description": "Mirats Insights Panel",
        "vendor_status": "active"
      }
    ],
    "createdAt": "2026-01-16T06:32:26.640Z",
    "updatedAt": "2026-01-16T06:32:26.640Z"
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="400 Bad Request">
{`{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Request validation failed",
    "details": [
      {
        "path": "surveyName",
        "message": "surveyName is required"
      },
      {
        "path": "setup.country",
        "message": "country is required"
      }
    ]
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="401 Unauthorized">
{`{
  "success": false,
  "error": {
    "code": "INVALID_API_KEY",
    "message": "API key is invalid, expired, or inactive"
  }
}`}</ResponseExample>
            </div>
          </div>
        </div>
      ),
    },
    bulkCreateSurveys: {
      title: "Bulk Create Surveys",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            Create multiple surveys, optionally within the same survey group. Useful for creating surveys across multiple countries or with different specifications.
          </p>
          
          <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
            <p className="text-white text-[14px] font-[500] mb-2">Endpoint</p>
            <code className="text-[#a1a1a6] text-[13px] font-mono">POST /surveys/bulk</code>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Headers</h3>
            <CodeBlock>{`Content-Type: application/json
X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Request Body Schema</h3>
            <CodeBlock>{`{
  "surveyGroupId": "optional-existing-group-id",
  "surveyGroupName": "Optional New Group Name",
  "surveyGroupDescription": "Optional group description",
  "surveys": [
    {
      "surveyName": "Survey 1 - US",
      "setup": {
        "country": "United States",
        "countryCode": "US",
        "language": "English"
      },
      "expectedMatrix": {
        "expectedCompletes": 100
      }
    },
    {
      "surveyName": "Survey 2 - UK",
      "setup": {
        "country": "United Kingdom",
        "countryCode": "GB",
        "language": "English"
      },
      "expectedMatrix": {
        "expectedCompletes": 150
      }
    }
  ]
}`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Required Fields</h3>
            <ul className="list-disc list-inside space-y-1 text-[#e5e5e5] text-[14px]">
              <li><code className="text-[#a1a1a6]">surveys</code> (array, min 1 survey)</li>
              <li>Each survey must follow the same requirements as single survey creation</li>
            </ul>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Response Examples</h3>
            <div className="space-y-6">
              <ResponseExample title="Success Response" status="201 Created">
{`{
  "success": true,
  "data": {
    "surveyGroup": {
      "_id": "group-id-if-created",
      "groupName": "Group Name",
      "description": "Group Description"
    },
    "surveys": [
      {
        "_id": "survey-id-1",
        "surveyName": "Survey 1 - US",
        "surveyNumber": 17685451455780061,
        "projectNumber": 17685451460530061,
        "surveyStatus": "awarded",
        "setup": {
          "country": "United States",
          "countryCode": "US",
          "language": "English",
          "studyType": "adhoc",
          "targetAudience": "B2C"
        },
        "expectedMatrix": {
          "expectedCompletes": 100,
          "expectedIncidenceRate": 50,
          "expectedCompletionLOI": 10
        },
        "peoples": {
          "projectManager": "John Doe"
        },
        "qualification": [
          {
            "_id": "qual-id-1",
            "question": "656d6d54f1cdf363aab57950",
            "validOptions": [{ "from": 18, "to": 75 }],
            "displayOptions": [],
            "status": true
          },
          {
            "_id": "qual-id-2",
            "question": "656d6d54f1cdf363aab57954",
            "validOptions": ["Male", "Female"],
            "displayOptions": ["Male", "Female", "others"],
            "status": true,
            "condition": { "min": "", "max": "" }
          }
        ],
        "allocation": [
          {
            "_id": "alloc-id-1",
            "allocation": {
              "number": 100,
              "percentage": 100
            },
            "cpi": 2.5,
            "description": "Mirats Insights Panel",
            "vendor_status": "active"
          }
        ],
        "createdAt": "2026-01-16T06:32:26.640Z",
        "updatedAt": "2026-01-16T06:32:26.640Z"
      }
    ]
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="400 Bad Request">
{`{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Request validation failed",
    "details": [
      {
        "path": "surveys",
        "message": "surveys array must contain at least 1 survey"
      }
    ]
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="404 Not Found">
{`{
  "success": false,
  "error": {
    "code": "SURVEY_GROUP_NOT_FOUND",
    "message": "Survey group not found or access denied"
  }
}`}</ResponseExample>
            </div>
          </div>
        </div>
      ),
    },
    getAllSurveys: {
      title: "Get All Surveys",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            Retrieve all surveys for your organization with pagination.
          </p>
          
          <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
            <p className="text-white text-[14px] font-[500] mb-2">Endpoint</p>
            <code className="text-[#a1a1a6] text-[13px] font-mono">GET /surveys</code>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Headers</h3>
            <CodeBlock>{`X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Query Parameters</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#2d2d2f]">
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Parameter</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Type</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Default</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>page</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">number</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">1</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Page number (min: 1)</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>limit</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">number</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">25</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Number of surveys per page (min: 1, max: 100)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Example Request</h3>
            <CodeBlock>{`GET /surveys?page=1&limit=10
X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Response Examples</h3>
            <div className="space-y-6">
              <ResponseExample title="Success Response" status="200 OK">
{`{
  "success": true,
  "data": {
    "surveys": [
      {
        "_id": "6969db791ca7128681350d0d",
        "surveyName": "My Survey",
        "surveyNumber": 17685451455780060,
        "projectNumber": 17685451460530060,
        "surveyStatus": "live",
        "setup": {
          "country": "United States",
          "countryCode": "US",
          "language": "English",
          "studyType": "adhoc",
          "targetAudience": "B2C"
        },
        "expectedMatrix": {
          "expectedCompletes": 100,
          "expectedIncidenceRate": 50,
          "expectedCompletionLOI": 10
        },
        "surveyGroup": {
          "_id": "group-id",
          "groupName": "Q1 Campaign",
          "description": "Q1 marketing surveys"
        },
        "createdAt": "2026-01-16T06:32:26.640Z",
        "updatedAt": "2026-01-16T06:32:26.640Z"
      }
    ],
    "pagination": {
      "page": 1,
      "limit": 10,
      "total": 50,
      "totalPages": 5
    }
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="400 Bad Request">
{`{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Request validation failed",
    "details": [
      {
        "path": "page",
        "message": "page must be at least 1"
      }
    ]
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="401 Unauthorized">
{`{
  "success": false,
  "error": {
    "code": "INVALID_API_KEY",
    "message": "API key is invalid, expired, or inactive"
  }
}`}</ResponseExample>
            </div>
          </div>
        </div>
      ),
    },
    getSingleSurvey: {
      title: "Get Single Survey",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            Retrieve details of a specific survey.
          </p>
          
          <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
            <p className="text-white text-[14px] font-[500] mb-2">Endpoint</p>
            <code className="text-[#a1a1a6] text-[13px] font-mono">GET /surveys/:surveyId</code>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Headers</h3>
            <CodeBlock>{`X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Path Parameters</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#2d2d2f]">
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Parameter</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Type</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>surveyId</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">string</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Survey ObjectId (required)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Example Request</h3>
            <CodeBlock>{`GET /surveys/6969db791ca7128681350d0d
X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Response Examples</h3>
            <div className="space-y-6">
              <ResponseExample title="Success Response" status="200 OK">
{`{
  "success": true,
  "data": {
    "_id": "6969db791ca7128681350d0d",
    "surveyName": "My Survey",
    "surveyNumber": 17685451455780060,
    "projectNumber": 17685451460530060,
    "surveyStatus": "live",
    "setup": {
      "country": "United States",
      "countryCode": "US",
      "language": "English",
      "studyType": "adhoc",
      "targetAudience": "B2C",
      "businessUnit": "Marketing",
      "industry": "retail",
      "personalInformation": false,
      "surveyCpi": "2.50",
      "clientCpi": "3.00",
      "testUrl": "https://example.com/test",
      "liveUrl": "https://example.com/live"
    },
    "expectedMatrix": {
      "expectedCompletes": 100,
      "expectedIncidenceRate": 50,
      "expectedCompletionLOI": 10
    },
    "peoples": {
      "projectManager": "John Doe"
    },
    "qualification": [
      {
        "_id": "qual-id-1",
        "question": "656d6d54f1cdf363aab57950",
        "validOptions": [{ "from": 18, "to": 75 }],
        "displayOptions": [],
        "status": true
      }
    ],
    "allocation": [
      {
        "_id": "alloc-id-1",
        "allocation": {
          "number": 100,
          "percentage": 100
        },
        "cpi": 2.5,
        "description": "Mirats Insights Panel",
        "vendor_status": "active"
      }
    ],
    "surveyGroup": {
      "_id": "group-id",
      "groupName": "Q1 Campaign",
      "description": "Q1 marketing surveys"
    },
    "createdAt": "2026-01-16T06:32:26.640Z",
    "updatedAt": "2026-01-16T06:32:26.640Z"
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="404 Not Found">
{`{
  "success": false,
  "error": {
    "code": "SURVEY_NOT_FOUND",
    "message": "Survey not found or access denied"
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="400 Bad Request">
{`{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Invalid survey ID format"
  }
}`}</ResponseExample>
            </div>
          </div>
        </div>
      ),
    },
    updateSurvey: {
      title: "Update Survey",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            Update survey details including status, URLs, CPI, and expected matrix.
          </p>
          
          <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
            <p className="text-white text-[14px] font-[500] mb-2">Endpoint</p>
            <code className="text-[#a1a1a6] text-[13px] font-mono">PUT /surveys/:surveyId</code>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Headers</h3>
            <CodeBlock>{`Content-Type: application/json
X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Path Parameters</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#2d2d2f]">
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Parameter</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Type</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>surveyId</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">string</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Survey ObjectId (required)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Request Body Schema</h3>
            <CodeBlock>{`{
  "surveyName": "Updated Survey Name",
  "surveyStatus": "live",
  "setup": {
    "liveUrl": "https://example.com/updated-live",
    "testUrl": "https://example.com/updated-test",
    "surveyCpi": "3.00",
    "clientCpi": "3.50",
    "country": "Canada",
    "countryCode": "CA",
    "language": "French"
  },
  "expectedMatrix": {
    "expectedCompletes": 200,
    "expectedIncidenceRate": 60,
    "expectedCompletionLOI": 15
  }
}`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Optional Fields</h3>
            <p className="text-[#a1a1a6] text-[13px] mb-2">At least one field must be provided for update. Only provided fields will be updated.</p>
            <ul className="list-disc list-inside space-y-1 text-[#e5e5e5] text-[14px]">
              <li><code className="text-[#a1a1a6]">surveyName</code> (string)</li>
              <li><code className="text-[#a1a1a6]">surveyStatus</code> (enum: "awarded", "live", "paused", "closed", "archived")</li>
              <li><code className="text-[#a1a1a6]">setup.liveUrl</code> (string, valid URL or empty string)</li>
              <li><code className="text-[#a1a1a6]">setup.testUrl</code> (string, valid URL or empty string)</li>
              <li><code className="text-[#a1a1a6]">expectedMatrix.expectedCompletes</code> (number, min 1)</li>
              <li><code className="text-[#a1a1a6]">expectedMatrix.expectedIncidenceRate</code> (number, 0-100)</li>
              <li><code className="text-[#a1a1a6]">expectedMatrix.expectedCompletionLOI</code> (number, min 0)</li>
            </ul>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Response Examples</h3>
            <div className="space-y-6">
              <ResponseExample title="Success Response" status="200 OK">
{`{
  "success": true,
  "data": {
    "_id": "6969db791ca7128681350d0d",
    "surveyName": "Updated Survey Name",
    "surveyNumber": 17685451455780060,
    "projectNumber": 17685451460530060,
    "surveyStatus": "live",
    "setup": {
      "country": "Canada",
      "countryCode": "CA",
      "language": "French",
      "studyType": "adhoc",
      "targetAudience": "B2C",
      "surveyCpi": "3.00",
      "clientCpi": "3.50",
      "testUrl": "https://example.com/updated-test",
      "liveUrl": "https://example.com/updated-live"
    },
    "expectedMatrix": {
      "expectedCompletes": 200,
      "expectedIncidenceRate": 60,
      "expectedCompletionLOI": 15
    },
    "updatedAt": "2026-01-16T07:00:00.000Z"
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="400 Bad Request">
{`{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "At least one field must be provided for update"
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="404 Not Found">
{`{
  "success": false,
  "error": {
    "code": "SURVEY_NOT_FOUND",
    "message": "Survey not found or access denied"
  }
}`}</ResponseExample>
            </div>
          </div>
        </div>
      ),
    },
    createSurveyGroup: {
      title: "Create Survey Group",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            Create a new survey group to organize related surveys.
          </p>
          
          <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
            <p className="text-white text-[14px] font-[500] mb-2">Endpoint</p>
            <code className="text-[#a1a1a6] text-[13px] font-mono">POST /survey-groups</code>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Headers</h3>
            <CodeBlock>{`Content-Type: application/json
X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Request Body Schema</h3>
            <CodeBlock>{`{
  "groupName": "Q1 2026 Campaign",
  "description": "Surveys for Q1 marketing campaign"
}`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Required Fields</h3>
            <ul className="list-disc list-inside space-y-1 text-[#e5e5e5] text-[14px]">
              <li><code className="text-[#a1a1a6]">groupName</code> (string, min 1 character)</li>
            </ul>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Response Examples</h3>
            <div className="space-y-6">
              <ResponseExample title="Success Response" status="201 Created">
{`{
  "success": true,
  "data": {
    "_id": "65ae286a4ca6dfa38e1d6d4f",
    "groupName": "Q1 2026 Campaign",
    "description": "Surveys for Q1 marketing campaign",
    "isActive": true,
    "surveys": [],
    "createdAt": "2026-01-16T06:32:26.640Z",
    "updatedAt": "2026-01-16T06:32:26.640Z"
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="400 Bad Request">
{`{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Request validation failed",
    "details": [
      {
        "path": "groupName",
        "message": "groupName is required"
      }
    ]
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="401 Unauthorized">
{`{
  "success": false,
  "error": {
    "code": "INVALID_API_KEY",
    "message": "API key is invalid, expired, or inactive"
  }
}`}</ResponseExample>
            </div>
          </div>
        </div>
      ),
    },
    getAllSurveyGroups: {
      title: "Get All Survey Groups",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            Retrieve all survey groups for your organization with pagination.
          </p>
          
          <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
            <p className="text-white text-[14px] font-[500] mb-2">Endpoint</p>
            <code className="text-[#a1a1a6] text-[13px] font-mono">GET /survey-groups</code>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Headers</h3>
            <CodeBlock>{`X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Query Parameters</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#2d2d2f]">
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Parameter</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Type</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Default</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>page</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">number</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">1</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Page number (min: 1)</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>limit</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">number</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">25</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Number of groups per page (min: 1, max: 100)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Example Request</h3>
            <CodeBlock>{`GET /survey-groups?page=1&limit=10
X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Response Examples</h3>
            <div className="space-y-6">
              <ResponseExample title="Success Response" status="200 OK">
{`{
  "success": true,
  "data": {
    "surveyGroups": [
      {
        "_id": "65ae286a4ca6dfa38e1d6d4f",
        "groupName": "Q1 2026 Campaign",
        "description": "Surveys for Q1 marketing campaign",
        "isActive": true,
        "surveys": ["6969db791ca7128681350d0d", "6969db791ca7128681350d0e"],
        "createdAt": "2026-01-16T06:32:26.640Z",
        "updatedAt": "2026-01-16T06:32:26.640Z"
      }
    ],
    "pagination": {
      "page": 1,
      "limit": 10,
      "total": 10,
      "totalPages": 1
    }
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="400 Bad Request">
{`{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Request validation failed",
    "details": [
      {
        "path": "page",
        "message": "page must be at least 1"
      }
    ]
  }
}`}</ResponseExample>
            </div>
          </div>
        </div>
      ),
    },
    getSingleSurveyGroup: {
      title: "Get Single Survey Group",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            Retrieve details of a specific survey group including associated surveys.
          </p>
          
          <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
            <p className="text-white text-[14px] font-[500] mb-2">Endpoint</p>
            <code className="text-[#a1a1a6] text-[13px] font-mono">GET /survey-groups/:groupId</code>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Headers</h3>
            <CodeBlock>{`X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Path Parameters</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#2d2d2f]">
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Parameter</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Type</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>groupId</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">string</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Survey Group ObjectId (required)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Example Request</h3>
            <CodeBlock>{`GET /survey-groups/65ae286a4ca6dfa38e1d6d4f
X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Response Examples</h3>
            <div className="space-y-6">
              <ResponseExample title="Success Response" status="200 OK">
{`{
  "success": true,
  "data": {
    "_id": "65ae286a4ca6dfa38e1d6d4f",
    "groupName": "Q1 2026 Campaign",
    "description": "Surveys for Q1 marketing campaign",
    "isActive": true,
    "surveys": [
      {
        "_id": "6969db791ca7128681350d0d",
        "surveyName": "Survey 1",
        "surveyNumber": 17685451455780060,
        "surveyStatus": "live"
      },
      {
        "_id": "6969db791ca7128681350d0e",
        "surveyName": "Survey 2",
        "surveyNumber": 17685451455780061,
        "surveyStatus": "awarded"
      }
    ],
    "createdAt": "2026-01-16T06:32:26.640Z",
    "updatedAt": "2026-01-16T06:32:26.640Z"
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="404 Not Found">
{`{
  "success": false,
  "error": {
    "code": "SURVEY_GROUP_NOT_FOUND",
    "message": "Survey group not found or access denied"
  }
}`}</ResponseExample>
            </div>
          </div>
        </div>
      ),
    },
    associateSurveyWithGroup: {
      title: "Associate Survey with Group",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            Associate an existing survey with a survey group.
          </p>
          
          <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
            <p className="text-white text-[14px] font-[500] mb-2">Endpoint</p>
            <code className="text-[#a1a1a6] text-[13px] font-mono">PUT /survey-groups/:groupId/surveys/:surveyId</code>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Headers</h3>
            <CodeBlock>{`X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Path Parameters</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#2d2d2f]">
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Parameter</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Type</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>groupId</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">string</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Survey Group ObjectId (required)</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>surveyId</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">string</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Survey ObjectId (required)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Example Request</h3>
            <CodeBlock>{`PUT /survey-groups/65ae286a4ca6dfa38e1d6d4f/surveys/6969db791ca7128681350d0d
X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Response Examples</h3>
            <div className="space-y-6">
              <ResponseExample title="Success Response" status="200 OK">
{`{
  "success": true,
  "data": {
    "message": "Survey successfully associated with survey group",
    "surveyGroup": {
      "_id": "65ae286a4ca6dfa38e1d6d4f",
      "groupName": "Q1 2026 Campaign",
      "surveys": ["6969db791ca7128681350d0d"]
    },
    "survey": {
      "_id": "6969db791ca7128681350d0d",
      "surveyName": "My Survey",
      "surveyGroup": "65ae286a4ca6dfa38e1d6d4f"
    }
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="404 Not Found">
{`{
  "success": false,
  "error": {
    "code": "SURVEY_NOT_FOUND",
    "message": "Survey not found or access denied"
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="404 Not Found">
{`{
  "success": false,
  "error": {
    "code": "SURVEY_GROUP_NOT_FOUND",
    "message": "Survey group not found or access denied"
  }
}`}</ResponseExample>
            </div>
          </div>
        </div>
      ),
    },
    getSessions: {
      title: "Get Sessions",
      content: (
        <div className="space-y-6">
          <p className="text-[#e5e5e5] text-[15px] leading-relaxed">
            Retrieve survey sessions with optional filtering and pagination.
          </p>
          
          <div className="bg-[#1d1d1f] border border-[#2d2d2f] rounded-lg p-4">
            <p className="text-white text-[14px] font-[500] mb-2">Endpoint</p>
            <code className="text-[#a1a1a6] text-[13px] font-mono">GET /sessions</code>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Headers</h3>
            <CodeBlock>{`X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Query Parameters</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#2d2d2f]">
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Parameter</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Type</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Default</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>surveyId</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">string</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">-</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Filter by survey ID (ObjectId)</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>status</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">number</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">-</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Filter by session status (mirats_status). See status codes below.</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>startDate</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">string (ISO 8601)</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">-</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Filter sessions from this date (e.g., "2026-01-01T00:00:00Z")</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>endDate</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">string (ISO 8601)</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">-</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Filter sessions until this date (e.g., "2026-01-31T23:59:59Z")</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>page</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">number</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">1</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Page number (min: 1)</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>limit</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">number</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">25</td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Number of sessions per page (min: 1, max: 100)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Example Request</h3>
            <CodeBlock>{`GET /sessions?surveyId=6969db791ca7128681350d0d&status=10&startDate=2026-01-01T00:00:00Z&endDate=2026-01-31T23:59:59Z&page=1&limit=25
X-API-Key: your-api-key-here`}</CodeBlock>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Response Examples</h3>
            <div className="space-y-6">
              <ResponseExample title="Success Response" status="200 OK">
{`{
  "success": true,
  "data": {
    "sessions": [
      {
        "_id": "session-id",
        "ref_id": "ref-12345",
        "mirats_status": 10,
        "client_status": 10,
        "survey": "survey-id",
        "surveyDetails": {
          "_id": "survey-id",
          "surveyName": "My Survey",
          "surveyNumber": 17685451455780060,
          "surveyStatus": "live"
        },
        "sessionTechnicalDetails": {
          "browserName": "Chrome",
          "os": "Windows"
        },
        "survey_start_time": "2026-01-16T10:00:00Z",
        "survey_end_time": "2026-01-16T10:15:00Z",
        "total_survey_time": "00:15:00",
        "geoData": {
          "time_zone": {
            "name": "America/New_York"
          }
        },
        "surveyCPI": 2.50,
        "responses": [],
        "createdAt": "2026-01-16T10:00:00Z"
      }
    ],
    "pagination": {
      "page": 1,
      "limit": 25,
      "total": 100,
      "totalPages": 4
    }
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="400 Bad Request">
{`{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Request validation failed",
    "details": [
      {
        "path": "status",
        "message": "Status must be a valid number"
      },
      {
        "path": "startDate",
        "message": "startDate must be a valid ISO 8601 date"
      }
    ]
  }
}`}</ResponseExample>

              <ResponseExample title="Error Response" status="401 Unauthorized">
{`{
  "success": false,
  "error": {
    "code": "INVALID_API_KEY",
    "message": "API key is invalid, expired, or inactive"
  }
}`}</ResponseExample>
            </div>
          </div>

          <div>
            <h3 className="text-white text-[18px] font-[600] mb-3">Session Status Codes</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#2d2d2f]">
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Code</th>
                    <th className="text-left text-white text-[14px] font-[600] py-2 px-4">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>10</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Complete</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>20</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Terminated</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>30</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Quota Full</td>
                  </tr>
                  <tr className="border-b border-[#2d2d2f]">
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4"><code>40</code></td>
                    <td className="text-[#a1a1a6] text-[13px] py-2 px-4">Quality Failed</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ),
    },
  };

  const navigationItems = [
    {
      id: "overview",
      title: "Overview",
      icon: <HiDocumentText size={16} />,
    },
    {
      id: "authentication",
      title: "Authentication",
      icon: <HiKey size={16} />,
      children: [
        { id: "authentication", title: "API Key Format" },
        { id: "responseFormat", title: "Response Format" },
        { id: "errorCodes", title: "Error Codes" },
      ],
    },
    {
      id: "commonLibrary",
      title: "Common Library",
      icon: <HiDocumentText size={16} />,
    },
    {
      id: "surveys",
      title: "Surveys",
      icon: <MdCode size={16} />,
      children: [
        { id: "surveyDefaults", title: "Survey Creation Defaults" },
        { id: "createSurvey", title: "Create Survey" },
        { id: "bulkCreateSurveys", title: "Bulk Create Surveys" },
        { id: "getAllSurveys", title: "Get All Surveys" },
        { id: "getSingleSurvey", title: "Get Single Survey" },
        { id: "updateSurvey", title: "Update Survey" },
      ],
    },
    {
      id: "surveyGroups",
      title: "Survey Groups",
      icon: <MdCode size={16} />,
      children: [
        { id: "createSurveyGroup", title: "Create Survey Group" },
        { id: "getAllSurveyGroups", title: "Get All Survey Groups" },
        { id: "getSingleSurveyGroup", title: "Get Single Survey Group" },
        { id: "associateSurveyWithGroup", title: "Associate Survey with Group" },
      ],
    },
    {
      id: "sessions",
      title: "Sessions",
      icon: <MdCode size={16} />,
      children: [
        { id: "getSessions", title: "Get Sessions" },
      ],
    },
  ];

  const currentContent = documentationContent[activeSection] || documentationContent.overview;

  return (
    <div className="flex flex-col h-screen bg-[#000000] text-white overflow-hidden">
      {/* Documentation Header */}
      <header className="bg-[#1d1d1f] border-b border-[#2d2d2f] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/profile/developer")}
            className="flex items-center gap-2 text-[#a1a1a6] hover:text-white text-[14px] transition-colors"
          >
            <MdArrowBack size={18} />
            <span>Back to Developer</span>
          </button>
          <div className="h-6 w-px bg-[#2d2d2f]"></div>
          <div className="flex items-center gap-3">
            <HiDocumentText size={20} className="text-white" />
            <h1 className="text-white text-[18px] font-[600]">Public DIY APIs Documentation</h1>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a1a1a6]" size={18} />
            <input
              type="text"
              placeholder="Search documentation..."
              className="bg-[#2d2d2f] border border-[#3d3d3f] rounded-lg pl-10 pr-4 py-2 text-white text-[14px] placeholder-[#6d6d6f] focus:outline-none focus:border-[#007aff] w-64"
            />
          </div>
          <div className="text-[#a1a1a6] text-[12px]">
            Version 1.0.0
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar Navigation */}
        <div className="w-64 bg-[#1d1d1f] border-r border-[#2d2d2f] flex flex-col overflow-y-auto">
          <div className="p-4 border-b border-[#2d2d2f]">
            <div className="flex items-center gap-2">
              <HiDocumentText size={18} className="text-white" />
              <h2 className="text-white text-[14px] font-[600]">Documentation</h2>
            </div>
          </div>

          <nav className="flex-1 p-4">
            <div className="space-y-1">
              {navigationItems.map((item) => (
                <div key={item.id}>
                  {item.children ? (
                    <>
                      <button
                        onClick={() => toggleSection(item.id)}
                        className="w-full flex items-center justify-between text-[#e5e5e5] hover:text-white text-[14px] py-2 px-3 rounded hover:bg-[#2d2d2f] transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          {item.icon}
                          <span>{item.title}</span>
                        </div>
                        {expandedSections[item.id] ? (
                          <MdKeyboardArrowDown size={16} />
                        ) : (
                          <MdChevronRight size={16} />
                        )}
                      </button>
                      {expandedSections[item.id] && (
                        <div className="ml-6 mt-1 space-y-1">
                          {item.children.map((child) => (
                            <button
                              key={child.id}
                              onClick={() => setActiveSection(child.id)}
                              className={`w-full text-left text-[13px] py-1.5 px-3 rounded transition-colors ${
                                activeSection === child.id
                                  ? "text-white bg-[#2d2d2f]"
                                  : "text-[#a1a1a6] hover:text-white hover:bg-[#2d2d2f]"
                              }`}
                            >
                              {child.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center gap-2 text-[14px] py-2 px-3 rounded transition-colors ${
                        activeSection === item.id
                          ? "text-white bg-[#2d2d2f]"
                          : "text-[#e5e5e5] hover:text-white hover:bg-[#2d2d2f]"
                      }`}
                    >
                      {item.icon}
                      <span>{item.title}</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-8">
            {/* Breadcrumbs */}
            <div className="mb-6 text-[#a1a1a6] text-[13px]">
              <span>Public DIY APIs / </span>
              <span className="text-white">{currentContent.title}</span>
            </div>

            {/* Main Title */}
            <h1 className="text-white text-[36px] font-[700] mb-4">{currentContent.title}</h1>

            {/* Content */}
            <div className="text-[#e5e5e5]">{currentContent.content}</div>

            {/* See Also Section */}
            {activeSection !== "overview" && (
              <div className="mt-12 pt-8 border-t border-[#2d2d2f]">
                <h3 className="text-white text-[18px] font-[600] mb-4">See Also</h3>
                <div className="space-y-2">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveSection("overview");
                    }}
                    className="text-[#007aff] hover:underline text-[14px] block"
                  >
                    Overview
                  </a>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveSection("authentication");
                    }}
                    className="text-[#007aff] hover:underline text-[14px] block"
                  >
                    Authentication
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiDocumentation;
